import React, { useMemo, useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Download, ShoppingCart, PlusCircle } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import MintedStatusBadge from "./MintedStatusBadge";

interface Solution {
  id: string;
  title: string;
  channel: string;
  creator: string;
  commercialType: "Sale" | "Lease" | "Collaborate" | "Stake";
  category: string;
  price: number;
  description: string;
  isMinted?: boolean;
}

const getCommercialTypeColor = (type: string) => {
  switch (type) {
    case "Sale":
      return "bg-blue-100 text-blue-800";
    case "Lease":
      return "bg-green-100 text-green-800";
    case "Collaborate":
      return "bg-purple-100 text-purple-800";
    case "Stake":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getCategoryColor = (category: string) => {
  if (category.includes("Construction")) return "bg-red-100 text-red-800";
  if (category.includes("Project")) return "bg-blue-100 text-blue-800";
  if (category.includes("Engineering")) return "bg-indigo-100 text-indigo-800";
  if (category.includes("Green")) return "bg-green-100 text-green-800";
  if (category.includes("Automation")) return "bg-purple-100 text-purple-800";
  if (category.includes("Health")) return "bg-orange-100 text-orange-800";
  if (category.includes("Data")) return "bg-cyan-100 text-cyan-800";
  if (category.includes("AI")) return "bg-violet-100 text-violet-800";
  if (category.includes("Machine")) return "bg-fuchsia-100 text-fuchsia-800";
  if (category.includes("Upstream")) return "bg-emerald-100 text-emerald-800";
  if (category.includes("Downstream")) return "bg-amber-100 text-amber-800";
  if (category.includes("Midstream")) return "bg-teal-100 text-teal-800";
  return "bg-gray-100 text-gray-800";
};

type Props = {
  initialSolutions: Solution[];
};

const IspaiMarketplaceView: React.FC<Props> = ({ initialSolutions }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewType, setViewType] = useState<"list" | "grid">("list");
  const [cart, setCart] = useState<Solution[]>([]);
  const [searchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const filteredSolutions = useMemo(() => {
    return initialSolutions.filter((s) =>
      [s.title, s.creator, s.category, s.description]
        .join(" ")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [initialSolutions, searchQuery]);

  useEffect(() => {
    const q = searchParams.get("q");
    const id = searchParams.get("id");
    if (q) setSearchQuery(q);
    if (id) {
      setHighlightId(id);
      setTimeout(() => setHighlightId(null), 3000);
      setTimeout(() => {
        const el = document.querySelector(`[data-solution-id="${id}"]`) as HTMLElement | null;
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addToCart = (solution: Solution) => {
    setCart((prev) => (prev.some((i) => i.id === solution.id) ? prev : [...prev, solution]));
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
  };

  const totalPrice = useMemo(() => cart.reduce((t, i) => t + i.price, 0), [cart]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-black mb-2">Marketplace</h1>
            <p className="text-gray-600">Discover solutions from all channels in the iSPAI ecosystem</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export
            </Button>
            <Button variant="outline" className="gap-2">
              <ShoppingCart className="h-4 w-4" />
              Cart
            </Button>
          </div>
        </div>

        <section className="mb-6">
          <h2 className="text-2xl font-semibold text-black">Knowledge Network and Marketplace</h2>
          <p className="text-gray-600">Browse 450+ specialized knowledge bases created by government officials, diplomats, and domain experts</p>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-9">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="relative w-full max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search solutions..."
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant={viewType === "list" ? "default" : "outline"} onClick={() => setViewType("list")} className="px-3">
                      List
                    </Button>
                    <Button variant={viewType === "grid" ? "default" : "outline"} onClick={() => setViewType("grid")} className="px-3">
                      Grid
                    </Button>
                  </div>
                </div>

                {viewType === "list" && (
                  <div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Solution</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Channel</TableHead>
                          <TableHead>Creator</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSolutions.map((solution) => (
                          <TableRow key={solution.id} data-solution-id={solution.id} className={highlightId === solution.id ? "ring-2 ring-primary rounded-md" : undefined}>
                            <TableCell className="align-top">
                              <div className="font-medium text-black">{solution.title}</div>
                              <div className="text-xs text-gray-500 mt-1">{solution.category}</div>
                              <div className="text-xs text-gray-500 mt-1 line-clamp-2">{solution.description}</div>
                            </TableCell>
                            <TableCell>
                              <MintedStatusBadge isMinted={solution.isMinted ?? false} />
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-gray-100 text-gray-800">
                                {solution.channel.toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{solution.creator}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getCommercialTypeColor(solution.commercialType)}>
                                {solution.commercialType}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-semibold">${""}{solution.price.toLocaleString()}</TableCell>
                            <TableCell>
                              <Button size="sm" onClick={() => addToCart(solution)} disabled={cart.some((i) => i.id === solution.id)}>
                                {cart.some((i) => i.id === solution.id) ? "Added" : "Add to Cart"}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {viewType === "grid" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredSolutions.map((solution) => (
                      <Card key={solution.id} data-solution-id={solution.id} className={`overflow-hidden ${highlightId === solution.id ? "ring-2 ring-primary rounded-md" : ""}`}>
                        <div className="p-4">
                          <div className="flex flex-wrap gap-2 items-center mb-3">
                            <MintedStatusBadge isMinted={solution.isMinted ?? false} />
                            <Badge variant="outline" className={getCategoryColor(solution.category)}>
                              {solution.category}
                            </Badge>
                            <Badge variant="outline" className={`ml-auto ${getCommercialTypeColor(solution.commercialType)}`}>
                              {solution.commercialType}
                            </Badge>
                          </div>
                          <h3 className="font-medium mb-1">{solution.title}</h3>
                          <p className="text-sm mb-4 text-gray-600 line-clamp-2">{solution.description}</p>
                          <div className="flex justify-between items-center">
                            <span className="font-semibold">${""}{solution.price.toLocaleString()}</span>
                            <Button size="sm" onClick={() => addToCart(solution)} disabled={cart.some((i) => i.id === solution.id)}>
                              {cart.some((i) => i.id === solution.id) ? "Added" : <PlusCircle className="h-4 w-4" />}
                            </Button>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">Creator: {solution.creator}</div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4" />
                  Your Cart ({cart.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {cart.length === 0 ? (
                  <p className="text-sm text-gray-500">Your cart is empty</p>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.id} className="flex justify-between items-center text-sm border-b pb-2">
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-gray-600">${""}{item.price.toLocaleString()}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeFromCart(item.id)}>
                          ×
                        </Button>
                      </div>
                    ))}
                    <div className="pt-2 border-t">
                      <div className="flex justify-between font-medium">
                        <span>Total:</span>
                        <span>${""}{totalPrice.toLocaleString()}</span>
                      </div>
                      <Button className="w-full mt-4">Checkout</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default IspaiMarketplaceView;
